import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Activity, Search, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MarketsPage() {
  const { getAuthHeaders } = useAuth();
  const [commodities, setCommodities] = useState([]);
  const [liveCommodities, setLiveCommodities] = useState([]);
  const [energyPrices, setEnergyPrices] = useState([]);
  const [techBenchmarks, setTechBenchmarks] = useState([]);
  const [insights, setInsights] = useState([]);
  const [intelligence, setIntelligence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [commoditySearch, setCommoditySearch] = useState('');
  const [commoditySort, setCommoditySort] = useState('name');
  const [energySearch, setEnergySearch] = useState('');
  const [energyRegion, setEnergyRegion] = useState('all');
  const [energySort, setEnergySort] = useState('country');
  const [techSort, setTechSort] = useState('attractiveness');
  const [insightFilter, setInsightFilter] = useState('all');

  useEffect(() => { fetchMarketData(); }, []);

  const fetchMarketData = async () => {
    try {
      const [commoditiesRes, energyRes, techRes, insightsRes, intelRes, liveRes] = await Promise.all([
        axios.get(`${API}/markets/commodities`, { headers: getAuthHeaders() }),
        axios.get(`${API}/markets/energy`, { headers: getAuthHeaders() }),
        axios.get(`${API}/markets/technology`, { headers: getAuthHeaders() }),
        axios.get(`${API}/markets/insights`, { headers: getAuthHeaders() }),
        axios.get(`${API}/markets/intelligence`, { headers: getAuthHeaders() }),
        axios.get(`${API}/markets/live-commodities`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setCommodities(commoditiesRes.data);
      setEnergyPrices(energyRes.data);
      setTechBenchmarks(techRes.data);
      setInsights(insightsRes.data);
      setIntelligence(intelRes.data);
      if (liveRes.data && liveRes.data.length > 0) {
        setLiveCommodities(liveRes.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (change) => change >= 0 ? <TrendingUp size={14} className="text-green-500" /> : <TrendingDown size={14} className="text-red-500" />;
  const getTrendColor = (change) => change >= 0 ? 'text-green-500' : 'text-red-500';
  const getSeverityColor = (severity) => {
    const colors = { info: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', opportunity: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', alert: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300', critical: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' };
    return colors[severity] || colors.info;
  };

  const filteredCommodities = useMemo(() => {
    let items = [...commodities];
    if (commoditySearch) items = items.filter(c => c.commodity.toLowerCase().includes(commoditySearch.toLowerCase()));
    items.sort((a, b) => {
      if (commoditySort === 'name') return a.commodity.localeCompare(b.commodity);
      if (commoditySort === 'price_asc') return a.price - b.price;
      if (commoditySort === 'price_desc') return b.price - a.price;
      if (commoditySort === 'change_24h') return b.change_24h - a.change_24h;
      if (commoditySort === 'change_30d') return b.change_30d - a.change_30d;
      return 0;
    });
    return items;
  }, [commodities, commoditySearch, commoditySort]);

  const energyRegions = useMemo(() => [...new Set(energyPrices.map(e => e.region || 'Unknown'))].sort(), [energyPrices]);

  const filteredEnergy = useMemo(() => {
    let items = [...energyPrices];
    if (energySearch) items = items.filter(e => e.country.toLowerCase().includes(energySearch.toLowerCase()));
    if (energyRegion !== 'all') items = items.filter(e => e.region === energyRegion);
    items.sort((a, b) => {
      if (energySort === 'country') return a.country.localeCompare(b.country);
      if (energySort === 'price_asc') return a.price - b.price;
      if (energySort === 'price_desc') return b.price - a.price;
      if (energySort === 'change') return b.change_30d - a.change_30d;
      return 0;
    });
    return items;
  }, [energyPrices, energySearch, energyRegion, energySort]);

  const sortedTech = useMemo(() => {
    let items = [...techBenchmarks];
    items.sort((a, b) => {
      if (techSort === 'attractiveness') return b.investment_attractiveness - a.investment_attractiveness;
      if (techSort === 'lcoe_asc') return a.lcoe - b.lcoe;
      if (techSort === 'lcoe_desc') return b.lcoe - a.lcoe;
      if (techSort === 'capex') return a.capex_per_kw - b.capex_per_kw;
      if (techSort === 'name') return a.technology.localeCompare(b.technology);
      return 0;
    });
    return items;
  }, [techBenchmarks, techSort]);

  const filteredInsights = useMemo(() => {
    if (insightFilter === 'all') return insights;
    return insights.filter(i => i.severity === insightFilter);
  }, [insights, insightFilter]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading market data...</div></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="markets-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">INMX Markets Intelligence</h1>
        <p className="text-muted-foreground mt-1">Live market data for infrastructure and natural minerals</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {intelligence.slice(0, 3).map((intel, index) => (
          <Card key={index} className="border-l-4" style={{ borderLeftColor: intel.impact_level === 'high' ? '#EF4444' : intel.impact_level === 'medium' ? '#F59E0B' : '#10B981' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium uppercase tracking-wider">{intel.category.replace('_', ' ')}</CardTitle>
                <Badge variant="outline" className="text-xs">{intel.impact_level}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono mb-1">{intel.commodity_or_sector}</div>
              <div className={cn('text-sm font-mono mb-2', getTrendColor(intel.change_pct))}>{intel.change_pct >= 0 ? '+' : ''}{intel.change_pct}%</div>
              <p className="text-xs text-muted-foreground">{intel.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="commodities" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="commodities">Commodities</TabsTrigger>
          <TabsTrigger value="energy">Energy Prices</TabsTrigger>
          <TabsTrigger value="technology">Technology</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="commodities" className="space-y-4">
          {/* Live Real-Time Prices from Yahoo Finance */}
          {liveCommodities.length > 0 && (
            <Card className="border-green-200 dark:border-green-900">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle>Real-Time Prices</CardTitle>
                    <Badge className="bg-green-100 text-green-700 text-[10px]">LIVE</Badge>
                    <Badge variant="outline" className="text-[9px]">Yahoo Finance</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {lastUpdated && <span className="text-[10px] text-muted-foreground">Updated {lastUpdated.toLocaleTimeString()}</span>}
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setLoading(true); fetchMarketData(); }} data-testid="refresh-prices-btn">
                      <Activity size={12} className="mr-1" />Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  {liveCommodities.map((item, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`live-price-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold">{item.label}</span>
                        <span className="text-[10px] text-muted-foreground">{item.unit}</span>
                      </div>
                      <div className="flex items-end justify-between">
                        <span className="text-xl font-mono font-bold">${item.price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        <div className={cn('flex items-center gap-1 text-sm font-mono', item.change_pct >= 0 ? 'text-green-500' : 'text-red-500')}>
                          {item.change_pct >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          <span>{item.change_pct >= 0 ? '+' : ''}{item.change_pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Seeded Reference Data */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Market Reference Data</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <Input value={commoditySearch} onChange={(e) => setCommoditySearch(e.target.value)} placeholder="Search..." className="h-8 pl-8 w-44 text-sm" data-testid="commodity-search" />
                  </div>
                  <Select value={commoditySort} onValueChange={setCommoditySort}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid="commodity-sort"><ArrowUpDown size={12} className="mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">A-Z</SelectItem>
                      <SelectItem value="price_desc">Price High-Low</SelectItem>
                      <SelectItem value="price_asc">Price Low-High</SelectItem>
                      <SelectItem value="change_24h">24h Change</SelectItem>
                      <SelectItem value="change_30d">30d Change</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredCommodities.map((commodity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-border rounded-sm hover:bg-muted/30 transition-colors" data-testid={`commodity-row-${index}`}>
                    <div className="flex-1">
                      <div className="font-semibold">{commodity.commodity}</div>
                      <div className="text-xs text-muted-foreground">{commodity.benchmark} / {commodity.exchange}</div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <div className="text-lg font-mono font-bold">{commodity.price.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{commodity.unit}</div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <div className={cn('flex items-center space-x-1 text-sm font-mono', getTrendColor(commodity.change_24h))}>
                          {getTrendIcon(commodity.change_24h)}
                          <span>{commodity.change_24h >= 0 ? '+' : ''}{commodity.change_24h}%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">24h</div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <div className={cn('flex items-center space-x-1 text-sm font-mono', getTrendColor(commodity.change_30d))}>
                          {getTrendIcon(commodity.change_30d)}
                          <span>{commodity.change_30d >= 0 ? '+' : ''}{commodity.change_30d}%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">30d</div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredCommodities.length === 0 && <p className="text-center text-muted-foreground py-4 text-sm">No commodities matching your search.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="energy" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Energy Prices by Region</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                    <Input value={energySearch} onChange={(e) => setEnergySearch(e.target.value)} placeholder="Search country..." className="h-8 pl-8 w-44 text-sm" data-testid="energy-search" />
                  </div>
                  <Select value={energyRegion} onValueChange={setEnergyRegion}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid="energy-region-filter"><SelectValue placeholder="All Regions" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {energyRegions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={energySort} onValueChange={setEnergySort}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid="energy-sort"><ArrowUpDown size={12} className="mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="country">A-Z</SelectItem>
                      <SelectItem value="price_desc">Price High-Low</SelectItem>
                      <SelectItem value="price_asc">Price Low-High</SelectItem>
                      <SelectItem value="change">30d Change</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredEnergy.map((energy, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-border rounded-sm hover:bg-muted/30 transition-colors" data-testid={`energy-row-${index}`}>
                    <div className="flex-1">
                      <div className="font-semibold">{energy.country}</div>
                      <div className="text-xs text-muted-foreground capitalize">{energy.energy_type?.replace('_', ' ')} / {energy.source}{energy.region ? ` / ${energy.region}` : ''}</div>
                    </div>
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <div className="text-lg font-mono font-bold">{energy.price.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">{energy.unit}</div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <div className={cn('flex items-center space-x-1 text-sm font-mono', getTrendColor(energy.change_7d))}>
                          {getTrendIcon(energy.change_7d)}<span>{energy.change_7d >= 0 ? '+' : ''}{energy.change_7d}%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">7d</div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <div className={cn('flex items-center space-x-1 text-sm font-mono', getTrendColor(energy.change_30d))}>
                          {getTrendIcon(energy.change_30d)}<span>{energy.change_30d >= 0 ? '+' : ''}{energy.change_30d}%</span>
                        </div>
                        <div className="text-xs text-muted-foreground">30d</div>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredEnergy.length === 0 && <p className="text-center text-muted-foreground py-4 text-sm">No energy prices matching your filters.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="technology" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Technology Benchmarks</CardTitle>
                <Select value={techSort} onValueChange={setTechSort}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="tech-sort"><ArrowUpDown size={12} className="mr-1" /><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attractiveness">Attractiveness (High-Low)</SelectItem>
                    <SelectItem value="lcoe_asc">LCOE (Low-High)</SelectItem>
                    <SelectItem value="lcoe_desc">LCOE (High-Low)</SelectItem>
                    <SelectItem value="capex">CapEx (Low-High)</SelectItem>
                    <SelectItem value="name">A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Technology</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">LCOE</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">CapEx/kW</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Capacity Factor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attractiveness</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedTech.map((tech, index) => (
                      <tr key={index} className="hover:bg-muted/30 transition-colors" data-testid={`tech-row-${index}`}>
                        <td className="px-4 py-3 font-medium">{tech.technology}</td>
                        <td className="px-4 py-3 text-right font-mono">${tech.lcoe}/MWh</td>
                        <td className="px-4 py-3 text-right font-mono">${tech.capex_per_kw.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-mono">{tech.capacity_factor}%</td>
                        <td className="px-4 py-3 text-right"><Badge variant="outline" className="font-mono">{tech.investment_attractiveness}/10</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="flex gap-2 mb-2">
            {['all', 'opportunity', 'alert', 'info', 'critical'].map(f => (
              <Button key={f} size="sm" variant={insightFilter === f ? 'default' : 'outline'} onClick={() => setInsightFilter(f)} className="capitalize text-xs" data-testid={`insight-filter-${f}`}>{f}</Button>
            ))}
          </div>
          {filteredInsights.map((insight, index) => (
            <Card key={index} data-testid={`insight-${index}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{insight.title}</CardTitle>
                  <Badge className={getSeverityColor(insight.severity)}>{insight.severity}</Badge>
                </div>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <Activity size={12} />
                  <span>AI-Generated / Confidence: {insight.confidence_score}/10</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed mb-4">{insight.content}</p>
                {(insight.related_commodities?.length > 0 || insight.related_regions?.length > 0 || insight.related_sectors?.length > 0) && (
                  <div className="flex flex-wrap gap-2">
                    {insight.related_commodities?.map((c, i) => <Badge key={`c${i}`} variant="outline" className="text-xs">{c}</Badge>)}
                    {insight.related_regions?.map((r, i) => <Badge key={`r${i}`} variant="outline" className="text-xs">{r}</Badge>)}
                    {insight.related_sectors?.map((s, i) => <Badge key={`s${i}`} variant="outline" className="text-xs">{s}</Badge>)}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {filteredInsights.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">No insights matching this filter.</CardContent></Card>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
